import { differenceInDays, parseISO, startOfDay, format } from "date-fns";

export interface SnapshotHotel {
  name: string;
  city: string | null;
  nights: number;
  roomType: string | null;
}

function nightsBetween(checkIn?: string | null, checkOut?: string | null): number {
  if (!checkIn || !checkOut) return 0;
  try {
    const n = differenceInDays(startOfDay(parseISO(checkOut)), startOfDay(parseISO(checkIn)));
    return n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function snapshotItems(snapshot: any): any[] {
  if (Array.isArray(snapshot)) return snapshot;
  if (snapshot && Array.isArray(snapshot.items)) return snapshot.items;
  return [];
}

/**
 * Accommodation properties featured in a quote's snapshot, in programme order,
 * de-duplicated by property name. Nights are summed per property.
 */
export function extractSnapshotHotels(snapshot: any): SnapshotHotel[] {
  const items = snapshotItems(snapshot)
    .filter((i: any) => i?.item_type === "accommodation")
    .filter((i: any) => i?.include_in_quote !== false)
    .filter((i: any) => i?.room_guest_type !== "staff")
    .sort((a: any, b: any) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0));

  const byName = new Map<string, SnapshotHotel>();
  for (const item of items) {
    const name = String(item?.supplier || item?.title || "").trim();
    if (!name) continue;
    const key = name.toLowerCase();
    const nights = nightsBetween(item?.check_in, item?.check_out);
    const existing = byName.get(key);
    if (existing) {
      existing.nights = Math.max(existing.nights, nights);
      if (!existing.city && item?.location) existing.city = item.location;
    } else {
      byName.set(key, {
        name,
        city: item?.location || null,
        nights,
        roomType: item?.room_rate?.room_type || null,
      });
    }
  }
  return Array.from(byName.values());
}

/** Total nights across the programme (falls back to accommodation nights). */
export function snapshotNights(quote: any, lead?: any): number {
  const start = quote?.snapshot_start_date || lead?.start_date;
  const end = quote?.snapshot_end_date || lead?.end_date;
  const fromDates = nightsBetween(start, end);
  if (fromDates > 0) return fromDates;
  return extractSnapshotHotels(quote?.itinerary_snapshot).reduce((s, h) => s + h.nights, 0);
}

export function quotePaxCount(quote: any, option?: { quantity?: number } | null, lead?: any): number | null {
  const pax = option?.quantity ?? quote?.option_quantity ?? quote?.pax_count ?? lead?.pax_count;
  const n = Number(pax);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Auto label: "QT-2026-218 · V&A Hotel, Sandton Sun, Mdluli +1 more · 8 nights · 20 pax"
 */
export function buildQuoteAutoLabel(
  quote: any,
  opts?: { option?: { label?: string; quantity?: number } | null; lead?: any; includeNumber?: boolean }
): string {
  const parts: string[] = [];
  if (opts?.includeNumber !== false && quote?.quote_number) parts.push(quote.quote_number);

  const hotels = extractSnapshotHotels(quote?.itinerary_snapshot);
  if (hotels.length > 0) {
    const shown = hotels.slice(0, 3).map((h) => h.name);
    const extra = hotels.length - shown.length;
    parts.push(shown.join(", ") + (extra > 0 ? ` +${extra} more` : ""));
  }

  const nights = snapshotNights(quote, opts?.lead);
  if (nights > 0) parts.push(`${nights} night${nights === 1 ? "" : "s"}`);

  const pax = quotePaxCount(quote, opts?.option, opts?.lead);
  if (pax) parts.push(`${pax} pax`);

  return parts.join(" · ");
}

/** The label shown to users: manual override when set, otherwise the auto label. */
export function quoteDisplayLabel(
  quote: any,
  opts?: {
    option?: { label?: string; quantity?: number } | null;
    lead?: any;
    includeNumber?: boolean;
  }
): string {
  const manual = (quote?.quote_label || "").trim();
  if (manual) return manual;
  return buildQuoteAutoLabel(quote, opts);
}

export function formatDateRangeShort(start?: string | null, end?: string | null): string | null {
  if (!start || !end) return null;
  try {
    return `${format(parseISO(start), "d MMM")} – ${format(parseISO(end), "d MMM yyyy")}`;
  } catch {
    return null;
  }
}

export interface OptionalExtra {
  description: string;
  price_per_person: number;
}

export function quoteOptionalExtras(quote: any): OptionalExtra[] {
  return Array.isArray(quote?.optional_extras)
    ? quote.optional_extras
        .filter((e: any) => e?.description && Number(e?.price_per_person) > 0)
        .map((e: any) => ({
          description: String(e.description),
          price_per_person: Number(e.price_per_person),
        }))
    : [];
}

/**
 * Occupancy wording for a per-person price, derived from the included accommodation
 * items rather than assumed: all single rooms → "per person (single room)",
 * any shared/double/twin rooms → "per person sharing", no accommodation → "per person".
 */
export function perPersonBasisLabel(items: any[] | null | undefined): string {
  const stays = (items || []).filter(
    (i: any) =>
      i?.item_type === "accommodation" &&
      i?.include_in_quote !== false &&
      i?.room_guest_type !== "staff" &&
      i?.pricing_treatment !== "alternative" && i?.pricing_treatment !== "optional" &&
      i?.accommodation_type !== "villas"
  );
  if (stays.length === 0) return "per person";
  const allSingle = stays.every((i: any) => String(i?.occupancy_type || "").toLowerCase() === "single");
  return allSingle ? "per person (single room)" : "per person sharing";
}

/** Label for the pricing style a quote was generated with. */
export function costPresentationLabel(quote: { cost_presentation?: string | null; show_line_items?: boolean | null }) {
  const p = quote?.cost_presentation || (quote?.show_line_items ? "full_breakdown" : "total_only");
  if (p === "total_only") return { key: "total_only", label: "Total only", description: "Client sees only the total investment." };
  if (p === "marked_up_items") return { key: "marked_up_items", label: "Marked-up items", description: "Item prices include markup; no markup or service-fee lines shown." };
  return { key: "full_breakdown", label: "Full breakdown", description: "Item prices plus markup and service-fee lines shown." };
}

/** "2 single rooms", "1 villa", "3 rooms" — room count is the item's quantity. */
export function roomCountLabel(item: { quantity?: number | null; occupancy_type?: string | null; accommodation_type?: string | null; room_allocations?: unknown }): string {
  const lines = Array.isArray(item.room_allocations) ? (item.room_allocations as any[]).filter((l) => l && Number(l.rooms) > 0) : [];
  if (lines.length > 0) {
    return lines.map((l) => {
      const r = Number(l.rooms);
      const occ = String(l.occupancy || "").trim().toLowerCase();
      return `${r} ${occ ? occ + " " : ""}${r === 1 ? "room" : "rooms"}`;
    }).join(", ");
  }
  const n = Math.max(1, Number(item.quantity) || 1);
  if (item.accommodation_type === "villas") return `${n} ${n === 1 ? "villa" : "villas"}`;
  const occ = (item.occupancy_type || "").trim().toLowerCase();
  return `${n} ${occ ? occ + " " : ""}${n === 1 ? "room" : "rooms"}`;
}
